export const isShipmentFullyPaid = (shipment) =>
  Number(shipment?.balance_amount ?? 0) <= 0;

export const QR_TERMINAL_STATUSES = ['delivered', 'rejected', 'returned'];

export const getQrTransitionEligibility = (shipment, targetStatus) => {
  const current = String(shipment?.status || '').toLowerCase();
  const target = String(targetStatus || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      eligible: false,
      skip: true,
      reason: `Cannot update ${shipment.status} shipment`,
    };
  }

  if (target === 'dispatched') {
    if (current === 'booked' && shipment?.can_dispatch) {
      return { eligible: true };
    }
    if (current === 'booked') {
      return {
        eligible: false,
        skip: true,
        reason: 'You can only dispatch your own shipment',
      };
    }
    return {
      eligible: false,
      skip: true,
      reason: 'Dispatch is only for booked shipments',
    };
  }

  if (target === 'received') {
    if (current === 'dispatched' || current === 'in transit' || current === 'received') {
      return {
        eligible: true,
        reapply: current === 'received',
      };
    }
    return {
      eligible: false,
      skip: true,
      reason: `Cannot change from ${shipment.status} to Received`,
    };
  }

  if (target === 'delivered') {
    if (current !== 'received') {
      return {
        eligible: false,
        skip: true,
        reason: `Must be Received first (current: ${shipment.status})`,
      };
    }
    if (shipment?.delivery_locked_until_settlement) {
      const ml = shipment.ml_sheet_id || 'this ML sheet';
      const isToPay = String(shipment.mode_of_payment || '').toLowerCase() === 'to_pay';
      return {
        eligible: false,
        skip: true,
        reason: isToPay
          ? `Settle ${ml} before receiving parcel`
          : `Settle ${ml} before delivery`,
      };
    }
    if (!isShipmentFullyPaid(shipment)) {
      return { eligible: false, skip: true, reason: 'Payment not complete' };
    }
    return { eligible: true };
  }

  return { eligible: false, skip: true, reason: 'Invalid status' };
};

export const getEligibleShipmentsForQrStatus = (shipments, targetStatus) => {
  const eligible = [];
  const skipped = [];

  for (const shipment of shipments || []) {
    const result = getQrTransitionEligibility(shipment, targetStatus);
    if (result.eligible) {
      eligible.push({ ...shipment, reapply: result.reapply });
    } else {
      skipped.push({ ...shipment, skipReason: result.reason });
    }
  }

  return { eligible, skipped };
};

export const getActiveQrShipments = (shipments) =>
  (shipments || []).filter(
    (s) => !QR_TERMINAL_STATUSES.includes(String(s.status || '').toLowerCase()),
  );

export const normalizeQrResolveResponse = (response) => {
  const data = response?.data || response;
  const shipments = Array.isArray(data?.shipments) && data.shipments.length
    ? data.shipments
    : data?.shipment_id
      ? [data]
      : [];

  return {
    shipments,
    failed: data?.failed || [],
    count: shipments.length,
    scanMethod: data?.scan_method || null,
  };
};

export const getQrStatusOptions = (resolved) => {
  if (!resolved) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }

  const current = String(resolved.status || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: `${resolved.status} shipments cannot be updated.`,
    };
  }

  if (current === 'booked') {
    if (resolved.can_dispatch) {
      return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
    }
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'You can only dispatch your own booked shipment.',
    };
  }

  if (current === 'dispatched' || current === 'in transit') {
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (current === 'received') {
    return {
      options: ['Received', 'Delivered'],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: isShipmentFullyPaid(resolved)
        ? 'Received can be confirmed again, or mark as Delivered.'
        : 'Received can be confirmed again. Delivered requires full payment.',
    };
  }

  return { options: [], autoSelect: null, paymentBlocked: false };
};

export const getBulkStatusOptions = (selectedIds, shipments) => {
  const selected = shipments.filter((s) => selectedIds.has(s.shipment_id));
  const active = getActiveQrShipments(selected);

  if (!active.length) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'All scanned shipments are already Delivered or cannot be updated.',
    };
  }

  const hasBookedDispatch = active.some(
    (s) => String(s.status).toLowerCase() === 'booked' && s.can_dispatch,
  );
  const hasDispatched = active.some(
    (s) => {
      const st = String(s.status).toLowerCase();
      return st === 'dispatched' || st === 'in transit';
    },
  );
  const hasReceived = active.some(
    (s) => String(s.status).toLowerCase() === 'received',
  );
  const hasUnpaid = active.some((s) => !isShipmentFullyPaid(s));

  if (hasBookedDispatch && !hasDispatched && !hasReceived) {
    return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
  }

  if (!hasReceived && hasDispatched && !hasBookedDispatch) {
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (hasReceived) {
    const options = ['Received', 'Delivered'];
    if (hasBookedDispatch) options.unshift('Dispatched');
    return {
      options,
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: hasUnpaid
        ? 'Received can be applied again. Delivered only for fully paid Received shipments.'
        : 'Received can be confirmed again, or mark Received shipments as Delivered.',
    };
  }

  if (hasBookedDispatch && hasDispatched) {
    return { options: ['Dispatched', 'Received'], autoSelect: null, paymentBlocked: false };
  }

  return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
};

export const getQrBatchStatusOptions = (shipments) => {
  if (!shipments?.length) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }
  if (shipments.length === 1) {
    return getQrStatusOptions(shipments[0]);
  }
  const selectedIds = new Set(shipments.map((s) => s.shipment_id));
  return getBulkStatusOptions(selectedIds, shipments);
};
